import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { AppRegistryPage } from '@plato/app-meta/ai-meta';
import { usePageAiMeta } from '@plato/app-shared/hooks/use-page-ai-meta';
import { FullPage404 } from '@plato/ui-legacy/components/common/full-page-404';
import { LoadingBlockFullPage } from '@plato/ui-legacy/components/loading/loading-block-full-page';
import { SectionHeader } from '@plato/ui-legacy/components/typography/section-header';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CustomerDetailHeader } from '../../../features/customer-detail/components/customer-detail-header.tsx';
import {
    CustomerDetailNavigation,
    CustomerDetailPathOverrideEnum,
} from '../../../features/customer-detail/components/customer-detail-navigation.tsx';
import { ExplorerViewActions } from '../../../features/explorer/components/explorer-view-actions.tsx';
import { ExplorerViewChart } from '../../../features/explorer/components/explorer-view-chart.tsx';
import { ExplorerViewRevenueStats } from '../../../features/explorer/components/explorer-view-revenue-stats.tsx';
import { ExplorerView } from '../../../features/explorer/components/explorer-view.tsx';
import { useAiChatExplorerColumns } from '../../../features/explorer/hooks/use-ai-chat-explorer-columns.ts';
import {
    ExplorerViewProvider,
    useExplorerQueryParams,
} from '../../../features/explorer/hooks/use-explorer-query-params.ts';
import { ExplorerTopLevelEntity } from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';

export function useCustomerInsightsAiMeta(): AppRegistryPage {
    const { t } = useTranslation();
    return usePageAiMeta(
        {
            path: '/customers/:customer_id/insights',
            title: t('navigation_tab__analysis', { ns: 'customer-detail' }),
            description:
                "The customer's analysis tab: the Sales Reports pivot scoped to this customer, plus " +
                '{{metric}} tiles per year and a chart against last year. Send the user here to break ' +
                "one customer's listed figures down by product, product group, industry, supplier, " +
                'storage location, clerk or merchandise group. For the same breakdown across all ' +
                'customers, use the Sales Reports page.',
        },
        useAiChatExplorerColumns(),
    );
}

function CustomerDetail() {
    const { t } = useTranslation();
    const { enableNewCustomerDetailView } = useAppConfig();
    const params = useParams<{ customer_id: string }>();
    const erpCustomerId = params.customer_id || '';

    const [state, setState] = useExplorerQueryParams(
        {
            tab: ExplorerTopLevelEntity.PRODUCT,
        },
        `explorer-${erpCustomerId}`,
    );

    const { data: customerBasics, isLoading: isLoadingCustomerBasics } =
        api.platoUser.getDetailCustomer.getBasics.useQuery({
            erpCustomerId,
        });

    if (isLoadingCustomerBasics) {
        return <LoadingBlockFullPage className=" mt-32" />;
    }

    if (!customerBasics) {
        return <FullPage404 />;
    }

    const overrideFilters = {
        customer: [erpCustomerId],
    };

    return (
        <Page className={enableNewCustomerDetailView ? '!px-gutter' : undefined}>
            <CustomerDetailHeader customer={customerBasics} />

            <CustomerDetailNavigation
                pathOverride={CustomerDetailPathOverrideEnum.INSIGHTS}
                customerId={erpCustomerId}
            />

            <ExplorerViewRevenueStats />

            <ExplorerViewChart overrideFilters={overrideFilters} hideTitles />

            <div className="flex w-full flex-col gap-3 overflow-hidden min-h-[calc(100dvh-1.5rem)]">
                <ExplorerViewProvider value={{ state, setState, defaultFilters: overrideFilters }}>
                    <SectionHeader
                        title={t('page_title__explorer')}
                        subTitle={t('page_sub_title__customer_explorer')}
                        actions={<ExplorerViewActions />}
                    />
                    <ExplorerView pageSource="customer" className="max-h-[90dvh]" />
                </ExplorerViewProvider>
            </div>
        </Page>
    );
}

export default CustomerDetail;
